import { Box, Typography } from "@mui/material";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useMemo, useState } from "react";
import { CodeIcon } from "../../../assets/images/icons/CodeIcon";
import DeltaIcon from "../../../assets/images/icons/DeltaIcon";
import InfoIcon from "../../../assets/images/icons/InfoIcon";
import { StructuredPromptIcon } from "../../../assets/images/icons/StructuredPromptIcon";
import BriaButtonGroup from "../../../components/common/BriaButtonGroup/BriaButtonGroup";
import BriaIconButton from "../../../components/common/BriaIconButton/BriaIconButton";
import CopyToClipboardButton from "../../../components/common/CopyToClipboardButton/CopyToClipboardButton";
import LoadingPlaceholder from "../../../components/common/LoadingPlaceholder/LoadingPlaceholder";
import { STRUCTURED_PROMPT_VIEW_MODES } from "../../../constants/FiboConstants";
import { useAppStore } from "../../../hooks/useStores";
import { JSONValue } from "../../../models/fibo";
import { isFibo2App } from "../../../utils";
import styles from "./FiboStructuredPrompt.module.scss";
import StructuredPromptView from "./StructuredPromptView/StructuredPromptView";

// type FiboStructuredPromptProps = {
// 	handleRefine: () => void;
// 	activeTab: "generate" | "refine";
// };

export interface IFiboStructuredPrompt {
	title: string;
	infoTooltip: string;
	jsonSourceViewTooltip: string;
	structuredViewTooltip: string;
	deltaPromptViewTooltip?: string;
	copyToClipboardTooltip: string;
	loadingPlaceholderText: string;
	wordCountText: string;
	emptyStateTitle: string;
	emptyStateDescription: string;
	activeTab?: "generate" | "refine";
}

const FiboStructuredPrompt = ({
	title,
	infoTooltip,
	jsonSourceViewTooltip,
	structuredViewTooltip,
	deltaPromptViewTooltip = "View Delta Prompt",
	copyToClipboardTooltip,
	loadingPlaceholderText,
	wordCountText,
	emptyStateTitle,
	emptyStateDescription,
	activeTab,
}: IFiboStructuredPrompt) => {
	const [mode, setMode] = useState<STRUCTURED_PROMPT_VIEW_MODES>();
	const { playgroundStore, fiboStore } = useAppStore();

	const selectedGroup = fiboStore.getSelectedFiboGroup();
	const selectedImage =
		selectedGroup?.selectedImage && "id" in selectedGroup.selectedImage ? selectedGroup.selectedImage : undefined;
	const isUploaded = playgroundStore.selectedResult?.id.startsWith("uploaded");
	const selectedResultId = playgroundStore.selectedResult?.id;
	const isRefineTabActive = activeTab === "refine";

	useEffect(() => {
		fiboStore.structuredPromptStore.setCurrentResultId(selectedResultId ?? null);
	}, [selectedResultId, fiboStore.structuredPromptStore, fiboStore]);

	const groupStructuredPrompt = selectedGroup?.structured_prompt;

	const structuredPrompt = useMemo(() => {
		if (!selectedGroup || !selectedImage || isUploaded) return null;
		if (!groupStructuredPrompt) return undefined;
		try {
			return JSON.parse(groupStructuredPrompt as string);
		} catch {
			return undefined;
		}
	}, [selectedGroup, selectedImage, groupStructuredPrompt, isUploaded]);

	const structuredPromptDiff = useMemo(() => {
		if (!selectedGroup?.structured_prompt_diff) return null;
		try {
			return JSON.parse(selectedGroup.structured_prompt_diff as string);
		} catch {
			return null;
		}
	}, [selectedGroup?.structured_prompt_diff]);

	const deltaPrompt = useMemo(() => {
		return selectedGroup?.delta_prompt ?? null;
	}, [selectedGroup?.delta_prompt]);

	const loading = useMemo(() => {
		if (!selectedGroup || !selectedImage) return fiboStore.isGeneratingStructuredPrompt;
		// FIBO 2 renders the image first, then derives the vgl — so the image already exists
		// while the vgl is still loading. Fall back to the global flag to show the spinner.
		if (isFibo2App()) return selectedGroup.is_structured_prompt_loading || fiboStore.isGeneratingStructuredPrompt;
		return selectedGroup.is_structured_prompt_loading;
	}, [selectedGroup, selectedImage, fiboStore.isGeneratingStructuredPrompt]);

	useEffect(() => {
		if (selectedGroup && selectedImage && !isUploaded) {
			setMode(STRUCTURED_PROMPT_VIEW_MODES.STRUCTURE);
		} else {
			setMode(undefined);
		}
	}, [selectedGroup, selectedImage, isUploaded]);

	const handleStructuredPromptChange = useCallback(
		(updatedData: JSONValue) => {
			const stringifiedPrompt = JSON.stringify(updatedData);

			// Update the group's shared structured prompt
			const selectedGroup = fiboStore.getSelectedFiboGroup();
			if (selectedGroup) {
				fiboStore.updateFiboResultGroup(selectedGroup.id, {
					structured_prompt: stringifiedPrompt,
				});
			}
		},
		[fiboStore],
	);

	const handleModeChange = (mode: STRUCTURED_PROMPT_VIEW_MODES) => {
		setMode(mode);
	};

	const jsonToCopy = useMemo(() => {
		// If viewing delta_prompt mode, copy the delta_prompt
		if (mode === STRUCTURED_PROMPT_VIEW_MODES.DELTA_PROMPT && deltaPrompt) {
			return deltaPrompt;
		}
		// Otherwise copy the structured_prompt
		const v = structuredPrompt;
		if (v == null) return "null";
		if (typeof v === "string") {
			try {
				const parsed = JSON.parse(v);
				return JSON.stringify(parsed, null, 2);
			} catch {
				return v;
			}
		}
		try {
			return JSON.stringify(v, null, 2);
		} catch {
			return String(v);
		}
	}, [structuredPrompt, mode, deltaPrompt]);

	return (
		<Box className={styles.structuredPrompt} data-vgl-editor>
			<Box className={styles.structuredPromptHeader}>
				<Box className={styles.titleContainer}>
					<Typography className={styles.title}>{title}</Typography>
					<BriaIconButton className={styles.infoIcon} title={infoTooltip}>
						<InfoIcon />
					</BriaIconButton>
				</Box>
				<BriaButtonGroup>
					{/* {structuredPrompt && activeTab === "generate" && (
						<BriaButton
							className={clsx(styles.actionButton, styles.refineButton)}
							buttonType="textMedium"
							startIcon={<EditIcon2 />}
							onClick={handleRefine}
							disabled={structuredPrompt === null}
						>
							{refineButtonText}
						</BriaButton>
					)} */}
					<BriaIconButton
						square
						className={clsx(styles.iconButton, styles.codeIcon)}
						title={
							mode === STRUCTURED_PROMPT_VIEW_MODES.JSON_SOURCE
								? structuredViewTooltip
								: jsonSourceViewTooltip
						}
						onClick={() =>
							handleModeChange(
								mode === STRUCTURED_PROMPT_VIEW_MODES.JSON_SOURCE
									? STRUCTURED_PROMPT_VIEW_MODES.STRUCTURE
									: STRUCTURED_PROMPT_VIEW_MODES.JSON_SOURCE,
							)
						}
						disabled={structuredPrompt === null}
					>
						{mode === STRUCTURED_PROMPT_VIEW_MODES.JSON_SOURCE ? <StructuredPromptIcon /> : <CodeIcon />}
					</BriaIconButton>
					{deltaPrompt && (
						<BriaIconButton
							square
							className={clsx(styles.iconButton, styles.deltaIcon)}
							title={
								mode === STRUCTURED_PROMPT_VIEW_MODES.DELTA_PROMPT
									? structuredViewTooltip
									: deltaPromptViewTooltip
							}
							onClick={() =>
								handleModeChange(
									mode === STRUCTURED_PROMPT_VIEW_MODES.DELTA_PROMPT
										? STRUCTURED_PROMPT_VIEW_MODES.STRUCTURE
										: STRUCTURED_PROMPT_VIEW_MODES.DELTA_PROMPT,
								)
							}
						>
							{mode === STRUCTURED_PROMPT_VIEW_MODES.DELTA_PROMPT ? (
								<StructuredPromptIcon />
							) : (
								<DeltaIcon />
							)}
						</BriaIconButton>
					)}
					<CopyToClipboardButton
						textToCopy={jsonToCopy}
						className={clsx(styles.iconButton)}
						tooltipText={copyToClipboardTooltip}
						disabled={structuredPrompt === null}
					/>
				</BriaButtonGroup>
			</Box>

			<LoadingPlaceholder
				isLoading={loading}
				className={clsx({
					[styles.loadingPlaceholder]: loading,
				})}
			>
				<StructuredPromptView
					data={structuredPrompt}
					previousData={isRefineTabActive && structuredPromptDiff ? structuredPromptDiff : undefined}
					deltaPrompt={deltaPrompt}
					viewMode={mode}
					isEditable={isRefineTabActive}
					textProps={{
						wordCountText,
						emptyStateTitle,
						emptyStateDescription,
					}}
					onStructuredPromptChange={handleStructuredPromptChange}
				/>
			</LoadingPlaceholder>
			{loading && <Typography className={styles.loadingPlaceholderText}>{loadingPlaceholderText}</Typography>}
		</Box>
	);
};

export default observer(FiboStructuredPrompt);
